import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BudgetAlert } from '../models/alert-framework.model';

@Component({
  selector: 'slt-alert-banner',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    @if (alerts && alerts.length > 0) {
      <div class="alerts-wrapper">
        @for (alert of alerts; track alert.id) {
          <div class="alert-banner severity-{{ alert.severity }}">
            <div class="alert-left">
              <mat-icon class="alert-icon">
                {{ alert.icon || getIconForSeverity(alert.severity) }}
              </mat-icon>
              <div class="alert-content">
                <span class="alert-title">{{ alert.title }}</span>
                <span class="alert-msg">{{ alert.message }}</span>
              </div>
            </div>

            <div class="alert-right">
              @for (act of alert.actions; track act.actionKey) {
                <button
                  mat-flat-button
                  [color]="act.isPrimary ? 'accent' : 'default'"
                  class="action-btn"
                  (click)="actionClicked.emit({ alertId: alert.id, actionKey: act.actionKey })"
                >
                  @if (act.icon) {
                    <mat-icon>{{ act.icon }}</mat-icon>
                  }
                  <span>{{ act.label }}</span>
                </button>
              }

              @if (alert.dismissible) {
                <button
                  mat-icon-button
                  class="dismiss-btn"
                  (click)="dismissed.emit(alert.id)"
                  matTooltip="Dismiss alert"
                >
                  <mat-icon>close</mat-icon>
                </button>
              }
            </div>
          </div>
        }
      </div>
    }
  `,
  styles: [
    `
      .alerts-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-bottom: 1rem;
        width: 100%;
      }

      .alert-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        border-left: 5px solid;
        flex-wrap: wrap;

        &.severity-warning {
          background: #fff8e1;
          border-left-color: #ffa000;
          color: #5d4037;
          .alert-icon {
            color: #ffa000;
          }
        }

        &.severity-danger {
          background: #ffebee;
          border-left-color: #d32f2f;
          color: #b71c1c;
          .alert-icon {
            color: #d32f2f;
          }
        }

        &.severity-info {
          background: #e3f2fd;
          border-left-color: #1976d2;
          color: #0d47a1;
          .alert-icon {
            color: #1976d2;
          }
        }

        &.severity-success {
          background: #e8f5e9;
          border-left-color: #388e3c;
          color: #1b5e20;
          .alert-icon {
            color: #388e3c;
          }
        }
      }

      .alert-left {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex: 1;
        min-width: 260px;
      }

      .alert-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        font-size: 0.88rem;

        .alert-title {
          font-weight: 700;
        }
        .alert-msg {
          line-height: 1.35;
        }
      }

      .alert-right {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .action-btn {
        font-size: 0.82rem;
        height: 32px;
      }

      .dismiss-btn {
        transform: scale(0.85);
        color: #757575;
      }
    `,
  ],
})
export class AlertBannerComponent {
  @Input() alerts: BudgetAlert[] = [];
  @Output() actionClicked = new EventEmitter<{ alertId: string; actionKey: string }>();
  @Output() dismissed = new EventEmitter<string>();

  getIconForSeverity(sev: string): string {
    switch (sev) {
      case 'warning':
        return 'warning_amber';
      case 'danger':
        return 'error_outline';
      case 'success':
        return 'check_circle_outline';
      default:
        return 'info';
    }
  }
}
